if (!customElements.get('break-archive-finder')) {
  class BreakArchiveFinder extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-break-archive-query]');
      this.clearButton = this.querySelector('[data-break-archive-clear]');
      this.availableOnly = this.querySelector('[data-break-archive-available]');
      this.results = this.querySelector('[data-break-archive-results]');
      this.status = this.querySelector('[data-break-archive-status]');
      this.artistList = this.querySelector('[data-break-archive-artists]');
      this.arrivals = this.querySelector('[data-break-archive-arrivals]');
      this.arrivalList = this.querySelector('[data-break-archive-arrival-list]');
      this.weekly = this.querySelector('[data-break-archive-weekly]');
      this.weeklyList = this.querySelector('[data-break-archive-weekly-list]');
      this.focusButtons = [...this.querySelectorAll('[data-break-archive-focus]')];
      this.activeFocus = '';
      this.newArrivalHandles = new Set();
      this.weeklyHandles = new Set();

      if (!this.input || !this.results || !this.status) return;

      this.products = [];
      this.input.addEventListener('input', () => this.render());
      this.clearButton?.addEventListener('click', () => {
        this.input.value = '';
        if (this.availableOnly) this.availableOnly.checked = false;
        this.activeFocus = '';
        this.focusButtons.forEach((button) => button.classList.remove('is-active'));
        this.render();
        this.input.focus();
      });
      this.availableOnly?.addEventListener('change', () => this.render());
      this.focusButtons.forEach((button) => {
        button.addEventListener('click', () => this.applyFocus(button.dataset.breakArchiveFocus || ''));
      });

      this.loadProducts();
    }

    uiText(key, values = {}) {
      const language = document.documentElement.lang;
      const locale = language === 'en' ? 'en' : language === 'zh-CN' ? 'zh-CN' : 'ja';
      const messages = {
        en: { loadError: 'Search data could not be loaded. Please use the work list below.', archive: 'Break Zenya Archive', available: 'Available', ask: 'Inquire (Ask)', loading: 'Loading archive works…', all: 'Search {count} works by artist, title or technique.', matches: '{count} works found.', empty: 'No matching works. Try a shorter search term.' },
        'zh-CN': { loadError: '无法加载搜索数据，请浏览下方作品列表。', archive: 'Break Zenya档案', available: '可购买', ask: '请咨询（Ask）', loading: '正在加载档案作品……', all: '可从{count}件作品中按艺术家、作品名称或技法搜索。', matches: '找到{count}件作品。', empty: '未找到符合条件的作品，请尝试更短的关键词。' },
        ja: { loadError: '検索データを読み込めませんでした。下の作品一覧をご覧ください。', archive: 'ブレイク前夜アーカイブ', available: '購入可能', ask: '要問い合わせ（Ask）', loading: 'アーカイブ作品を読み込んでいます。', all: '全{count}点から、作家名・作品名・技法で探せます。', matches: '{count}点見つかりました。', empty: '該当する作品がありません。表記を短くしてお試しください。' }
      };
      return messages[locale][key].replace(/\{(\w+)\}/g, (_, name) => String(values[name] ?? ''));
    }

    async loadProducts() {
      try {
        const endpoint = new URL(this.dataset.endpoint, window.location.origin);
        endpoint.searchParams.set('_', Date.now().toString());
        const response = await fetch(endpoint, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
        });

        if (!response.ok) throw new Error(`Archive request failed: ${response.status}`);

        const payload = await response.json();
        this.products = (payload.products || []).map((product) => this.prepareProduct(product));
        this.renderNewArrivals();
        this.renderWeeklyFocus();
        this.renderArtistList();

        const initialArtist = new URLSearchParams(window.location.search).get('archive_artist');
        if (initialArtist) this.input.value = initialArtist;

        this.render();
      } catch (error) {
        this.status.textContent = this.uiText('loadError');
        this.results.hidden = true;
      }
    }

    prepareProduct(product) {
      const artist = this.extractArtist(product);
      const plainBody = this.stripHtml(product.body_html || '');
      const tags = Array.isArray(product.tags) ? product.tags.join(' ') : String(product.tags || '');
      const askAvailability = /(?:^|[,\s])rw-availability-ask(?:$|[,\s])/.test(tags);
      const available = !askAvailability && (product.variants || []).some((variant) => variant.available);
      const prices = (product.variants || [])
        .map((variant) => Number(variant.price))
        .filter((price) => Number.isFinite(price));

      return {
        artist,
        askAvailability,
        available,
        handle: product.handle,
        image: product.images?.[0]?.src || '',
        price: prices.length ? Math.min(...prices) : null,
        publishedAt: product.published_at ? new Date(product.published_at) : null,
        searchText: this.normalize([artist, product.title, tags, plainBody].join(' ')),
        title: product.title,
        displayTitle: product.title?.startsWith(artist) ? product.title.slice(artist.length).trim() : product.title,
      };
    }

    extractArtist(product) {
      if (product.body_html) {
        const documentFragment = new DOMParser().parseFromString(product.body_html, 'text/html');
        const rows = [...documentFragment.querySelectorAll('tr')];
        const artistRow = rows.find((row) => {
          const label = row.querySelector('th, td')?.textContent || '';
          return label.includes('作家') || /artist/i.test(label);
        });
        const cells = artistRow ? artistRow.querySelectorAll('th, td') : [];
        const artistCell = cells.length > 1 ? cells[1].textContent.trim() : '';
        if (artistCell) return artistCell.split(/\s*[\/／|｜]\s*/)[0].trim();
      }

      const bracketMatch = product.title?.match(/^(.+?)[「『]/);
      if (bracketMatch) return bracketMatch[1].trim();

      return product.title?.split(/\s+/)[0]?.trim() || '';
    }

    stripHtml(value) {
      return new DOMParser().parseFromString(value, 'text/html').body.textContent || '';
    }

    normalize(value) {
      return String(value || '')
        .normalize('NFKC')
        .toLocaleLowerCase('ja')
        .replace(/\s+/g, ' ')
        .trim();
    }

    renderNewArrivals() {
      if (!this.arrivals || !this.arrivalList) return;

      const dayInMilliseconds = 24 * 60 * 60 * 1000;
      const newArrivalDays = Math.max(1, Number(this.dataset.newArrivalDays) || 30);
      const newArrivalLimit = Math.max(1, Number(this.dataset.newArrivalLimit) || 3);
      const now = Date.now();
      const futureTolerance = 5 * 60 * 1000;
      const newArrivals = this.products
        .filter((product) => {
          const publishedAt = product.publishedAt?.getTime();
          if (!Number.isFinite(publishedAt)) return false;
          const age = now - publishedAt;
          return age >= -futureTolerance && age <= newArrivalDays * dayInMilliseconds;
        })
        .sort((a, b) => b.publishedAt.getTime() - a.publishedAt.getTime())
        .slice(0, newArrivalLimit);

      this.arrivalList.replaceChildren();
      newArrivals.forEach((product) => this.arrivalList.appendChild(this.createArrivalCard(product)));
      this.newArrivalHandles = new Set(newArrivals.map((product) => product.handle));
      this.arrivals.hidden = newArrivals.length === 0;
    }

    renderWeeklyFocus() {
      if (!this.weekly || !this.weeklyList) return;

      const allAvailableProducts = this.products
        .filter((product) => product.available)
        .sort((a, b) => {
          const publishedDifference = (b.publishedAt?.getTime() || 0) - (a.publishedAt?.getTime() || 0);
          return publishedDifference || a.title.localeCompare(b.title, 'ja');
        });
      const archiveFocusProducts = allAvailableProducts.filter((product) => !this.newArrivalHandles.has(product.handle));
      const availableProducts = archiveFocusProducts.length >= 3 ? archiveFocusProducts : allAvailableProducts;
      const weekInMilliseconds = 7 * 24 * 60 * 60 * 1000;
      const campaignStart = Date.UTC(2026, 7, 31);
      const weekIndex = Math.max(0, Math.floor((Date.now() - campaignStart) / weekInMilliseconds));
      const startIndex = availableProducts.length ? (weekIndex * 3) % availableProducts.length : 0;
      const weeklyProducts = [];

      for (let index = 0; index < availableProducts.length && weeklyProducts.length < 3; index += 1) {
        weeklyProducts.push(availableProducts[(startIndex + index) % availableProducts.length]);
      }

      this.weeklyHandles = new Set(weeklyProducts.map((product) => product.handle));
      this.weeklyList.replaceChildren();
      weeklyProducts.forEach((product) => this.weeklyList.appendChild(this.createWeeklyCard(product)));
      this.weekly.hidden = weeklyProducts.length === 0;
    }

    createWeeklyCard(product) {
      const item = this.createArrivalCard(product);
      const badge = item.querySelector('.rw-baf__arrival-badge');
      if (badge) badge.textContent = 'THIS WEEK';
      return item;
    }

    applyFocus(focus) {
      this.activeFocus = this.activeFocus === focus ? '' : focus;
      this.focusButtons.forEach((button) => {
        button.classList.toggle('is-active', button.dataset.breakArchiveFocus === this.activeFocus);
      });

      if (this.activeFocus === 'weekly') {
        this.weekly?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      if (this.activeFocus) {
        this.input.value = '';
        if (this.availableOnly) this.availableOnly.checked = true;
      }
      this.render();
      this.input.focus({ preventScroll: true });
    }

    createArrivalCard(product) {
      const item = document.createElement('li');
      item.className = 'rw-baf__arrival-card';

      const link = document.createElement('a');
      link.className = 'rw-baf__arrival-link';
      link.href = this.productUrl(product.handle);

      const media = document.createElement('span');
      media.className = 'rw-baf__arrival-media';
      const badge = document.createElement('span');
      badge.className = 'rw-baf__arrival-badge';
      badge.textContent = `NEW · ${this.formatArrivalDate(product.publishedAt)}`;
      media.appendChild(badge);

      if (product.image) {
        const image = document.createElement('img');
        image.src = product.image;
        image.alt = product.title;
        image.loading = 'lazy';
        image.width = 720;
        image.height = 540;
        media.appendChild(image);
      }

      const copy = document.createElement('span');
      copy.className = 'rw-baf__arrival-copy';
      const titleGroup = document.createElement('span');
      titleGroup.className = 'rw-baf__arrival-title';
      const artist = document.createElement('small');
      artist.textContent = product.artist || this.uiText('archive');
      const title = document.createElement('strong');
      title.textContent = product.displayTitle || product.title;
      titleGroup.append(artist, title);

      const meta = document.createElement('span');
      meta.className = 'rw-baf__arrival-meta';
      const price = document.createElement('span');
      price.className = 'rw-baf__arrival-price';
      price.textContent = this.formatPrice(product.price);
      const availability = document.createElement('span');
      availability.className = `rw-baf__arrival-availability${product.available ? '' : product.askAvailability ? ' is-ask rw-ask-label' : ' is-sold rw-sold-label'}`;
      availability.textContent = product.available ? this.uiText('available') : product.askAvailability ? this.uiText('ask') : 'SOLD';
      meta.append(price, availability);

      copy.append(titleGroup, meta);
      link.append(media, copy);
      item.appendChild(link);
      return item;
    }

    formatArrivalDate(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
      return new Intl.DateTimeFormat('ja-JP', {
        month: 'numeric',
        day: 'numeric',
        timeZone: 'Asia/Tokyo',
      })
        .format(date)
        .replace('/', '.');
    }

    formatPrice(price) {
      if (!Number.isFinite(price)) return '';
      return `¥${new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 0 }).format(price)}`;
    }

    productUrl(handle) {
      const rootUrl = (this.dataset.rootUrl || '/').replace(/\/?$/, '/');
      return `${rootUrl}products/${encodeURIComponent(handle)}`;
    }

    renderArtistList() {
      if (!this.artistList) return;

      const artists = [...new Set(this.products.map((product) => product.artist).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, 'ja')
      );

      this.artistList.replaceChildren();
      artists.forEach((artist) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'rw-baf__artist-chip';
        button.textContent = artist;
        button.addEventListener('click', () => {
          this.input.value = artist;
          this.render();
          this.input.focus();
        });
        this.artistList.appendChild(button);
      });
    }

    render() {
      if (!this.products.length) {
        this.status.textContent = this.uiText('loading');
        return;
      }

      const query = this.normalize(this.input.value);
      const tokens = query.split(' ').filter(Boolean);
      const availableOnly = Boolean(this.availableOnly?.checked);

      if (!query && !availableOnly && !this.activeFocus) {
        this.results.replaceChildren();
        this.results.hidden = true;
        this.status.textContent = this.uiText('all', { count: this.products.length });
        this.clearButton?.toggleAttribute('hidden', true);
        return;
      }

      const matches = this.products.filter((product) => {
        const matchesQuery = tokens.every((token) => product.searchText.includes(token));
        const matchesAvailability = !availableOnly || product.available;
        const matchesFocus = this.matchesFocus(product);
        return matchesQuery && matchesAvailability && matchesFocus;
      });

      this.results.replaceChildren();
      matches.forEach((product) => this.results.appendChild(this.createResult(product)));
      this.results.hidden = false;
      this.status.textContent = matches.length
        ? this.uiText('matches', { count: matches.length })
        : this.uiText('empty');
      this.clearButton?.toggleAttribute('hidden', false);
    }

    matchesFocus(product) {
      if (!this.activeFocus) return true;
      if (this.activeFocus === 'under300') return product.available && Number.isFinite(product.price) && product.price <= 300000;
      if (this.activeFocus === 'under500') return product.available && Number.isFinite(product.price) && product.price <= 500000;
      if (this.activeFocus === 'over1m') return product.available && Number.isFinite(product.price) && product.price >= 1000000;
      if (this.activeFocus === 'sculpture') {
        return product.available && ['立体', '彫刻', 'sculpture', 'three-dimensional'].some((term) => product.searchText.includes(term));
      }
      if (this.activeFocus === 'weekly') return this.weeklyHandles.has(product.handle);
      return true;
    }

    createResult(product) {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'rw-baf__result';
      link.href = this.productUrl(product.handle);

      const media = document.createElement('span');
      media.className = 'rw-baf__result-media';
      if (product.image) {
        const image = document.createElement('img');
        image.src = product.image;
        image.alt = '';
        image.loading = 'lazy';
        image.width = 120;
        image.height = 120;
        media.appendChild(image);
      }

      const copy = document.createElement('span');
      copy.className = 'rw-baf__result-copy';
      const artist = document.createElement('small');
      artist.textContent = product.artist || this.uiText('archive');
      const title = document.createElement('strong');
      title.textContent = product.title;
      const availability = document.createElement('span');
      availability.className = product.available ? 'is-available' : product.askAvailability ? 'is-ask rw-ask-label' : 'is-sold rw-sold-label';
      availability.textContent = product.available ? this.uiText('available') : product.askAvailability ? this.uiText('ask') : 'SOLD';
      copy.append(artist, title, availability);

      const arrow = document.createElement('span');
      arrow.className = 'rw-baf__result-arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '→';

      link.append(media, copy, arrow);
      item.appendChild(link);
      return item;
    }
  }

  customElements.define('break-archive-finder', BreakArchiveFinder);
}
